<template>
    <div class="top-header">
        <div class="brand" :class="{ collapsed: store.state.isCollapse }" @click="goDashboard">
            <el-icon class="brand-icon"><Grid /></el-icon>
            <span v-if="!store.state.isCollapse" class="brand-title">{{ systemTitle }}</span>
        </div>
        <div class="header-main">
            <el-icon class="collapse-btn" @click="changeCollapse">
                <Expand v-if="store.state.isCollapse" />
                <Fold v-else />
            </el-icon>
            <div class="spacer"></div>
            <el-popover placement="bottom-end" width="238" trigger="click" popper-class="theme-popover">
                <template #reference>
                    <el-icon class="theme-btn"><Setting /></el-icon>
                </template>
                <div class="theme-panel">
                    <div class="theme-title">{{ themeTitle }}</div>
                    <div class="theme-colors">
                        <button
                            v-for="item in themeColors"
                            :key="item.value"
                            class="theme-color"
                            :class="{ active: themeColor === item.value }"
                            :style="{ backgroundColor: item.value }"
                            :title="item.label"
                            @click="setThemeColor(item.value)">
                            <span v-if="themeColor === item.value">&#10003;</span>
                        </button>
                    </div>
                    <el-color-picker
                        v-model="themeColor"
                        :predefine="themeColors.map(item => item.value)"
                        @change="setThemeColor"
                    />
                </div>
            </el-popover>
            <span class="welcome">{{ welcomeText }}</span>
            <el-dropdown>
                <span class="el-dropdown-link">
                    <el-icon :size="24" color="white">
                        <User />
                    </el-icon>
                </span>
                <template #dropdown>
                    <el-dropdown-menu>
                        <el-dropdown-item @click="handleLogout">{{ logoutText }}</el-dropdown-item>
                    </el-dropdown-menu>
                </template>
            </el-dropdown>
        </div>
    </div>
</template>
<script setup>
import { computed, onMounted, ref } from "vue";
import { Expand, Fold, Grid, Setting, User } from "@element-plus/icons-vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { logOutFun } from "@/api/index";

const THEME_STORAGE_KEY = "admin_theme_color";
const DEFAULT_THEME_COLOR = "#2d3a4b";
const router = useRouter();
const store = useStore();
const systemTitle = "\u9502\u7814\u6570\u79d1\u540e\u53f0\u7ba1\u7406\u7cfb\u7edf";
const logoutText = "\u9000\u51fa\u767b\u5f55";
const themeTitle = "\u4e3b\u9898\u8272";
const themeColor = ref(DEFAULT_THEME_COLOR);
const themeColors = [
    { label: "\u9ed8\u8ba4\u6df1\u84dd", value: "#2d3a4b" },
    { label: "\u5546\u52a1\u84dd", value: "#2563eb" },
    { label: "\u9752\u7eff", value: "#0f766e" },
    { label: "\u79d1\u6280\u7d2b", value: "#7c3aed" },
    { label: "\u6696\u7ea2", value: "#b91c1c" },
    { label: "\u78b3\u9ed1", value: "#111827" },
];
const welcomeText = computed(() => "\u6b22\u8fce " + (store.state.userInfo.userName || "") + " \u56de\u6765");

const changeCollapse = () => {
    store.commit("changeCollapse");
};

const goDashboard = () => {
    router.push("/dashboard");
};

const setThemeColor = (color) => {
    if (!color) {
        return;
    }
    themeColor.value = color;
    localStorage.setItem(THEME_STORAGE_KEY, color);
    applyThemeColor(color);
};

const applyThemeColor = (color) => {
    const root = document.documentElement;
    root.style.setProperty("--admin-theme-color", color);
    root.style.setProperty("--admin-theme-dark", shadeColor(color, -14));
    root.style.setProperty("--admin-theme-hover", shadeColor(color, 8));
    root.style.setProperty("--admin-theme-light", hexToRgba(color, 0.1));
    root.style.setProperty("--el-color-primary", color);
    root.style.setProperty("--el-color-primary-dark-2", shadeColor(color, -16));
    root.style.setProperty("--el-color-primary-light-3", hexToRgba(color, 0.7));
    root.style.setProperty("--el-color-primary-light-5", hexToRgba(color, 0.5));
    root.style.setProperty("--el-color-primary-light-7", hexToRgba(color, 0.3));
    root.style.setProperty("--el-color-primary-light-8", hexToRgba(color, 0.2));
    root.style.setProperty("--el-color-primary-light-9", hexToRgba(color, 0.1));
};

const shadeColor = (hex, percent) => {
    const normalized = normalizeHex(hex);
    const num = parseInt(normalized.slice(1), 16);
    const amount = Math.round(2.55 * percent);
    const r = Math.max(0, Math.min(255, (num >> 16) + amount));
    const g = Math.max(0, Math.min(255, ((num >> 8) & 0x00ff) + amount));
    const b = Math.max(0, Math.min(255, (num & 0x0000ff) + amount));
    return "#" + (0x1000000 + r * 0x10000 + g * 0x100 + b).toString(16).slice(1);
};

const hexToRgba = (hex, alpha) => {
    const normalized = normalizeHex(hex);
    const num = parseInt(normalized.slice(1), 16);
    const r = num >> 16;
    const g = (num >> 8) & 0x00ff;
    const b = num & 0x0000ff;
    return "rgba(" + r + ", " + g + ", " + b + ", " + alpha + ")";
};

const normalizeHex = (hex) => {
    if (/^#[0-9a-fA-F]{6}$/.test(hex)) {
        return hex;
    }
    if (/^#[0-9a-fA-F]{3}$/.test(hex)) {
        return "#" + hex.slice(1).split("").map((item) => item + item).join("");
    }
    return DEFAULT_THEME_COLOR;
};

const handleLogout = () => {
    logOutFun().finally(() => {
        localStorage.removeItem("token");
        store.commit("clearUserInfo");
        router.push("/login");
    });
};

onMounted(() => {
    const savedColor = localStorage.getItem(THEME_STORAGE_KEY) || DEFAULT_THEME_COLOR;
    setThemeColor(savedColor);
});
</script>
<style lang="scss" scoped>
.top-header {
    height: 60px;
    width: 100%;
    display: flex;
    background-color: var(--admin-theme-color, #2d3a4b);
    color: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    z-index: 2;
}

.brand {
    width: 200px;
    height: 60px;
    flex: 0 0 200px;
    display: flex;
    align-items: center;
    padding: 0 18px;
    box-sizing: border-box;
    background-color: var(--admin-theme-color, #2d3a4b);
    transition: width 0.2s ease, flex-basis 0.2s ease;
    overflow: hidden;
    cursor: pointer;
    user-select: none;
}

.brand.collapsed {
    width: 64px;
    flex-basis: 64px;
    justify-content: center;
    padding: 0;
}

.brand-icon {
    flex: 0 0 auto;
    font-size: 18px;
}

.brand-title {
    margin-left: 8px;
    font-size: 16px;
    font-weight: 600;
    white-space: nowrap;
}

.header-main {
    min-width: 0;
    flex: 1;
    display: flex;
    align-items: center;
    padding: 0 20px;
    box-sizing: border-box;
}

.collapse-btn,
.theme-btn {
    font-size: 22px;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.92);
}

.collapse-btn:hover,
.theme-btn:hover {
    color: #fff;
}

.spacer {
    flex: 1;
}

.theme-btn {
    margin-right: 18px;
}

.welcome {
    margin-right: 12px;
    font-size: 14px;
    white-space: nowrap;
}

.el-dropdown {
    cursor: pointer;
}

.el-dropdown-link {
    display: flex;
    align-items: center;
    outline: none;
}

.theme-panel {
    padding: 2px 0;
}

.theme-title {
    margin-bottom: 12px;
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
}

.theme-colors {
    display: flex;
    gap: 10px;
    margin-bottom: 14px;
}

.theme-color {
    width: 28px;
    height: 28px;
    border: 2px solid transparent;
    border-radius: 50%;
    color: #fff;
    cursor: pointer;
    line-height: 24px;
    text-align: center;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
}

.theme-color.active {
    border-color: #fff;
    outline: 2px solid #111827;
}
</style>
